// Fondos de reserva (Arts. 196/201 CT): reporte mensual sobre el rol del
// período — quién tiene derecho (>1 año), cuánto se pagó mensualizado en el
// rol y cuánto debe depositarse en el IESS. Nómina → Beneficios Sociales.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { MESES, TipoRolSelect, fecha, fieldCls, money, nomRpc, type FondosReservaData } from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';

const HOY = new Date();

export function FondosReserva() {
  const [anio, setAnio] = useState(HOY.getFullYear());
  const [mes, setMes] = useState(HOY.getMonth() + 1);
  const [tipo, setTipo] = useState('');
  const { data, isFetching } = useQuery<FondosReservaData>({
    queryKey: ['nom-fondos-reserva', anio, mes, tipo],
    queryFn: () => nomRpc<FondosReservaData>('nom_fondos_reserva', { _anio: anio, _mes: mes, _tipo: tipo || null }),
  });

  const filas = data?.empleados ?? [];
  const totDepositar = filas.reduce((s, f) => s + (f.con_derecho && f.modalidad === 'acumulado' ? Number(f.fondo) : 0), 0);
  const totPagado = filas.reduce((s, f) => s + Number(f.pagado_rol), 0);
  const totBase = filas.reduce((s, f) => s + Number(f.base), 0);
  const totFondo = filas.reduce((s, f) => s + (f.con_derecho ? Number(f.fondo) : 0), 0);

  const headersExp = ['N°', 'Código', 'Empleado', 'Ingreso', 'Derecho', 'Modalidad', 'Base', 'Fondo 8.33%', 'Pagado en rol', 'Depositar IESS'];
  const MONEYCOLS = [6, 7, 8, 9];
  const filasExport: Celda[][] = [
    ...filas.map((f, i): Celda[] => [
      i + 1, f.empleado_codigo ?? '', f.razon_social, fecha(f.fecha_ingreso),
      f.con_derecho ? 'Sí' : 'Aún no', f.modalidad === 'mensualizado' ? 'Mensualizado' : 'Depósito IESS',
      Number(f.base), f.con_derecho ? Number(f.fondo) : '', Number(f.pagado_rol) > 0 ? Number(f.pagado_rol) : '',
      f.con_derecho && f.modalidad === 'acumulado' ? Number(f.fondo) : '',
    ]),
    ['', '', '', '', '', 'TOTAL', totBase, totFondo, totPagado, totDepositar],
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Fondos de Reserva</h1>
        <div className="flex items-center gap-2">
          <select className={`${fieldCls} w-36`} value={mes} onChange={(e) => setMes(Number(e.target.value))} aria-label="Mes">
            {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input type="number" className={`${fieldCls} w-24`} value={anio} onChange={(e) => setAnio(Number(e.target.value))} aria-label="Año" />
          <TipoRolSelect value={tipo} onChange={setTipo} />
          <ExportarBotones
            nombre={`fondos-de-reserva-${fechaLocal()}`} titulo="Fondos de Reserva"
            headers={headersExp} filas={filasExport} colsDerecha={[0, 6, 7, 8, 9]} disabled={filas.length === 0}
            fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Art. 196 CT: 8.33% de la remuneración desde el segundo año de servicios. Modalidad «acumulado» se deposita
        mensualmente en el IESS (Art. 201); «mensualizado» ya se pagó en el rol (rubro FR_MEN).
      </p>

      {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}

      {!isFetching && !data?.rol && (
        <Card><CardContent className="py-10 text-center text-muted-foreground">
          No hay rol generado para {MESES[mes - 1]} {anio}. El reporte se calcula sobre el rol del período.
        </CardContent></Card>
      )}

      {data?.rol && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Card><CardContent className="py-3"><div className="text-xs text-muted-foreground">Rol {data.rol.codigo}</div><div className="text-lg font-semibold">{data.rol.estado}</div></CardContent></Card>
            <Card><CardContent className="py-3"><div className="text-xs text-muted-foreground">A depositar en el IESS</div><div className="text-lg font-semibold font-mono tabular-nums">{money(totDepositar)}</div></CardContent></Card>
            <Card><CardContent className="py-3"><div className="text-xs text-muted-foreground">Pagado mensualizado en el rol</div><div className="text-lg font-semibold font-mono tabular-nums">{money(totPagado)}</div></CardContent></Card>
          </div>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Detalle ({filas.length})</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Ingreso</th>
                  <th className="py-2 pr-3">Derecho</th><th className="py-2 pr-3">Modalidad</th>
                  <th className="py-2 pr-3 text-right">Base</th><th className="py-2 pr-3 text-right">Fondo 8.33%</th>
                  <th className="py-2 pr-3 text-right">Pagado en rol</th><th className="py-2 pr-3 text-right">Depositar IESS</th>
                </tr></thead>
                <tbody>
                  {filas.map((f, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2 pr-3 font-mono">{f.empleado_codigo}</td>
                      <td className="py-2 pr-3">{f.razon_social}<div className="text-xs text-muted-foreground font-mono">{f.identificacion}</div></td>
                      <td className="py-2 pr-3 font-mono">{fecha(f.fecha_ingreso)}</td>
                      <td className="py-2 pr-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${f.con_derecho ? 'bg-estable-soft text-estable' : 'bg-muted text-muted-foreground'}`}>
                          {f.con_derecho ? 'Sí' : 'Aún no'}
                        </span>
                      </td>
                      <td className="py-2 pr-3">{f.modalidad === 'mensualizado' ? 'Mensualizado' : 'Depósito IESS'}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.base)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{f.con_derecho ? money(f.fondo) : '—'}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums text-muted-foreground">{Number(f.pagado_rol) > 0 ? money(f.pagado_rol) : '—'}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">
                        {f.con_derecho && f.modalidad === 'acumulado' ? money(f.fondo) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
